import moment from "moment-timezone";
import { separateConstants } from "utilities/constants";

const { onboardingModalTypes, accountStatusTypes } = separateConstants;

const formattingActionText = (obj, onRowTextClickHandler) => {
  const { type, text } = obj;

  if (type === "nothing") {
    return <span className="regular-text greyed-text">{text}</span>;
  }
  if (type === "modal") {
    return (
      <span
        onClick={() => onRowTextClickHandler(obj)}
        className="regular-text module-text"
      >
        {text}
      </span>
    );
  }
};

const tableFormattedDataForEachElement = (
  item,
  onRowTextClickHandler,
  TGRequestsList,
  includeTraderId
) => {
  const {
    personal,
    createdAt,
    password,
    statusUpdatedAt,
    firstEnableRequest,
    software,
    softwareType,
    marketDataRequest,
    status,
    _id,
    reference,
    initialWire,
    agreement
  } = item;

  const formattedData = moment(createdAt).format("YYYY-MM-DD");
  let formattedCurrentState = "N/A";
  let formattedActions = "N/A";
  let formattedCreds = formattingActionText({ type: "nothing", text: "N/A" });

  const currentState = () => {
    const hasCompletedMDRequest = TGRequestsList?.some(
      req => req.applicationID?._id === _id && 
            req.type === "MARKETDATA" && 
            req.status === "COMPLETED"
    );
    const hasExistingTradingAccess = TGRequestsList?.some(
      req => req.applicationID?._id === _id && 
            req.type === "TRADING_ACCESS"
    );

    const isInitialWireReceived = initialWire?.status === 'received';

    if (
        !isInitialWireReceived
        && status === accountStatusTypes.APPROVED
        && initialWire
    ) {
        return 'wireNotReceived';
    }

    if (status === accountStatusTypes.ACCOUNT_CREATED && 
        hasCompletedMDRequest && 
        !hasExistingTradingAccess) {
      return "readyForEnableRequest";
    }
    if (status === accountStatusTypes.INCOMPLETE && !!software) {
      return "notApproved";
    }
    if (status === accountStatusTypes.INCOMPLETE && !software) {
      return "tpNotSelected";
    }
    if (status === accountStatusTypes.APPROVED && software) {
      return "tpPending";
    }
    if (
      status === accountStatusTypes.ACCOUNT_CREATED &&
      software === "Sterling" &&
      !!marketDataRequest
    ) {
      return "sterlingAgreementsNotFilled";
    }
    if (status === accountStatusTypes.ACCOUNT_CREATED && marketDataRequest) {
      return "pendingMD";
    }
    if (
      status === accountStatusTypes.ACCOUNT_CREATED &&
      firstEnableRequest &&
      !firstEnableRequest.schedule?.duedate
    ) {
      return "selectStartDate";
    }
    if (
      status === accountStatusTypes.ACCOUNT_CREATED &&
      firstEnableRequest &&
      firstEnableRequest.schedule?.duedate
    ) {
      return "enableOn";
    }
    // Handle simulator accounts that are enabled and ready for upgrade
    if (status === accountStatusTypes.ACCOUNT_ENABLED && 
        software === "Sterling" && 
        softwareType === "Sim") {
      return "simulatorEnabled";
    }
    
    if (status === accountStatusTypes.ACCOUNT_ENABLED) {
      return "enabledOn";
    }
    
    // Handle simulator accounts that are created but don't have market data or enable requests
    if (status === accountStatusTypes.ACCOUNT_CREATED && 
        software === "Sterling" && 
        softwareType === "Sim" && 
        !marketDataRequest && 
        !firstEnableRequest) {
      return "simulatorCreated";
    }
    
    // Handle simulator accounts that have enable request but no due date
    if (status === accountStatusTypes.ACCOUNT_CREATED && 
        software === "Sterling" && 
        softwareType === "Sim" && 
        firstEnableRequest && 
        !firstEnableRequest.schedule?.duedate) {
      return "simulatorSelectStartDate";
    }
    
    // Handle Business deal status - newly created live account
    if (status === accountStatusTypes.BUSINESS_DEAL) {
      return "businessDeal";
    }
    
    // Default case for any other AccountCreated status
    if (status === accountStatusTypes.ACCOUNT_CREATED) {
      return "accountCreated";
    }
  };

  switch (currentState()) {
    case "readyForEnableRequest":
      formattedCurrentState = `Created on ${moment(createdAt).format("MMMM DD, YYYY")}`;
      formattedActions = (
        <span>
          To create enable request&nbsp;
          {formattingActionText(
            {
              type: "modal",
              modalType: onboardingModalTypes.EFFECTIVE_DATE,
              text: "click here",
              item,
            },
            onRowTextClickHandler
          )}
        </span>
      );
      break;
    case "notComplete":
      formattedActions = "Awaiting the trader to submit the application";
      formattedCurrentState = "Application not completed";
      break;
    case "notApproved":
      formattedActions = "Contact Zimtra Support Team";
      formattedCurrentState = "Application not approved";
      break;
    case "tpNotSelected":
      formattedActions = formattingActionText(
        {
          type: "modal",
          modalType: onboardingModalTypes.TRADING_PACKAGE,
          text: "Select trading package options",
          item,
        },
        onRowTextClickHandler
      );
      formattedCurrentState = "Trading package options not selected";
      break;
    case "tpPending":
      formattedActions = formattingActionText({
        type: "nothing",
        text: "Nothing. Zimtra is working on it",
      });
      formattedCurrentState = "Pending trading platform";
      break;
    case "sterlingAgreementsNotFilled":
      formattedActions = formattingActionText(
        {
          type: "modal",
          modalType: onboardingModalTypes.EXCHANGE_AGREEMENTS,
          text: "Fill out exchange agreements with Sterling",
          item,
        },
        onRowTextClickHandler
      );
      formattedCurrentState = "Exchange agreements not filled out";
      break;
    case "pendingMD":
      formattedActions = formattingActionText({
        type: "nothing",
        text: "Nothing. Zimtra is working on it",
      });
      formattedCurrentState = "Pending market data";
      break;
      case "wireNotReceived":
          const amount = initialWire?.amount?.pending || agreement?.rules?.performanceBond;

          formattedActions = formattingActionText({
              type: "nothing",
              text: `Pending $${ amount } performance bond`,
          });
          formattedCurrentState = "Wire is not received";
          break;
    case "selectStartDate":
      formattedActions = formattingActionText(
        {
          type: "modal",
          modalType: onboardingModalTypes.EFFECTIVE_DATE,
          text: "Select a start date",
          item,
        },
        onRowTextClickHandler
      );
      formattedCurrentState = "Start date not selected";
      break;
    case "enableOn":
      formattedActions = (
        <span>
          To change this date &nbsp;
          {formattingActionText(
            {
              type: "modal",
              modalType: onboardingModalTypes.EFFECTIVE_DATE,
              text: "click here",
              item,
            },
            onRowTextClickHandler
          )}
        </span>
      );
      formattedCurrentState = `To be enabled on ${moment
        .utc(firstEnableRequest.schedule?.duedate)
        .format("MMMM DD, YYYY")}`;
      break;
    case "enabledOn":
      formattedActions = formattingActionText({
        type: "nothing",
        text: "Ready to trade!",
      });
      formattedCurrentState = `Enabled on ${moment(statusUpdatedAt).format(
        "MMMM DD, YYYY"
      )}`;
      break;
    case "simulatorCreated":
      formattedCurrentState = `Created on ${moment(createdAt).format("MMMM DD, YYYY")}`;
      formattedActions = formattingActionText(
        {
          type: "modal",
          modalType: onboardingModalTypes.EFFECTIVE_DATE,
          text: "Select start date",
          item,
        },
        onRowTextClickHandler
      );
      break;
    case "simulatorSelectStartDate":
      formattedCurrentState = `Created on ${moment(createdAt).format("MMMM DD, YYYY")}`;
      formattedActions = formattingActionText(
        {
          type: "modal",
          modalType: onboardingModalTypes.EFFECTIVE_DATE,
          text: "Select start date",
          item,
        },
        onRowTextClickHandler
      );
      break;
    case "simulatorEnabled":
      formattedCurrentState = `Enabled on ${moment(statusUpdatedAt).format("MMMM DD, YYYY")}`;
      formattedActions = (
        <div>
          <div className="greyed-text" style={{ marginBottom: '8px' }}>
            Ready to trade!
          </div>
          {formattingActionText(
            {
              type: "modal",
              modalType: onboardingModalTypes.UPGRADE_CONFIRMATION,
              text: "Upgrade to live account",
              item,
            },
            onRowTextClickHandler
          )}
        </div>
      );
      break;
    case "businessDeal":
      formattedCurrentState = `Created on ${moment(createdAt).format("MMMM DD, YYYY")}`;
      formattedActions = formattingActionText(
        {
          type: "modal",
          modalType: onboardingModalTypes.TRADING_PACKAGE,
          text: "Select trading package options",
          item,
        },
        onRowTextClickHandler
      );
      break;
    case "accountCreated":
      formattedCurrentState = `Created on ${moment(createdAt).format("MMMM DD, YYYY")}`;
      formattedActions = formattingActionText({
        type: "nothing",
        text: "Account created - awaiting next steps",
      });
      break;

    default:
      formattedCurrentState = "N/A";
      break;
  }

  if (password) {
    formattedCreds = formattingActionText(
      {
        type: "modal",
        modalType: onboardingModalTypes.CREDS,
        text: "See credentials",
        item,
      },
      onRowTextClickHandler
    );
  }

  const baseRow = {
    "Full Name": `${personal.firstName} ${personal.lastName}` || "N/A",
    email: personal.email || "N/A",
    "Date Created": formattedData || "N/A",
    // Subgroup: "N/A",
    "Current State": formattedCurrentState || "N/A",
    "My Necessary Action": formattedActions || "N/A",
    "Trading Platform Credentials": formattedCreds || "N/A",
  };

  if (!includeTraderId) return baseRow;

  return {
    TraderID: reference?.client || "N/A",
    ...baseRow,
  };
};

const tableFormattedData = (onboardingItems, onRowTextClickHandler, TGRequestsList) => {
  if (onboardingItems?.length) {
    // Filter out simulator accounts if there's a live account with the same email
    const filteredItems = onboardingItems.filter((item) => {
      // If this is a simulator account, check if there's a live account with the same email
      if (item.softwareType === "Sim") {
        const hasLiveAccount = onboardingItems.some((otherItem) => 
          otherItem.personal?.email === item.personal?.email && 
          otherItem.softwareType !== "Sim" &&
          otherItem._id !== item._id
        );
        // Hide simulator account if live account exists
        return !hasLiveAccount;
      }
      // Keep all non-simulator accounts
      return true;
    });

    const includeTraderId = filteredItems.some((i) => !!i?.reference?.client);

    return filteredItems?.map((item) =>
      tableFormattedDataForEachElement(
        item,
        onRowTextClickHandler,
        TGRequestsList,
        includeTraderId
      )
    );
  }

  return [
    {
      TraderID: "N/A",
      "Full Name": "N/A",
      email: "N/A",
      "Date Created": "N/A",
      Subgroup: "N/A",
      "Current State": "N/A",
      "My Necessary Action": "N/A",
      "Trading Platform Credentials": "N/A",
    },
    "no-data",
  ];
};

const onboardingHelpers = {
  tableFormattedData,
};

export default onboardingHelpers;
